import React from 'react';
import {Box, IconButton, InputBase, Paper, Tooltip, Typography} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Floating in-document search bar for the file viewer.
 *
 * Appears at the top of the document pane when the user presses Ctrl+F / ⌘+F.
 * Provides a text input, match counter ("3 of 17"), Prev/Next navigation buttons and
 * a close (×) button.
 *
 * Props:
 *   query         {string}   - controlled input value
 *   setQuery      {fn}       - called on input change
 *   matchCount    {number}   - total number of matches
 *   activeIdx     {number}   - 0-based active match index (-1 = none)
 *   onNavigate    {fn}       - called with 'next' | 'prev'
 *   onClose       {fn}       - called when the bar should be dismissed
 *   hasDualPane   {boolean}  - when true the viewer shows English (left) + original (right);
 *                              the bar anchors to the left edge so it only covers the English pane.
 */
const DocumentSearchBar = React.forwardRef(function DocumentSearchBar(
    {query, setQuery, matchCount, activeIdx, onNavigate, onClose, hasDualPane = false},
    ref,
) {
    const inputRef = React.useRef(null);

    // Autofocus the input when the bar mounts.
    React.useEffect(() => {
        const timer = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(timer);
    }, []);

    const handleKeyDown = React.useCallback((e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose?.();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            onNavigate?.(e.shiftKey ? 'prev' : 'next');
        }
    }, [onClose, onNavigate]);

    const queryTrimmed = query.trim();
    const searchActive = queryTrimmed.length >= 2;
    const noResults = searchActive && matchCount === 0;
    const counterText = !searchActive
        ? ''
        : matchCount === 0
            ? 'No results'
            : `${activeIdx + 1} of ${matchCount}`;

    return (
        <Paper
            ref={ref}
            elevation={4}
            sx={{
                position: 'absolute',
                top: 56,
                ...(hasDualPane ? {right: 'calc(50% + 12px)'} : {right: 12}),
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                minWidth: 300,
                maxWidth: 420,
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.shadows[6],
                border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            {/* Search icon */}
            <SearchIcon
                sx={{
                    fontSize: 18,
                    color: noResults ? 'error.main' : 'text.disabled',
                    flexShrink: 0,
                    mr: 0.5,
                }}
            />

            {/* Text input */}
            <InputBase
                inputRef={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Find in document…"
                inputProps={{'aria-label': 'Find in document'}}
                sx={{
                    flex: 1,
                    fontSize: '0.875rem',
                    '& input': {
                        p: 0,
                        color: noResults ? 'error.main' : 'text.primary',
                    },
                }}
            />

            {/* Match counter */}
            <Typography
                variant="caption"
                sx={{
                    flexShrink: 0,
                    minWidth: 52,
                    textAlign: 'right',
                    color: noResults ? 'error.main' : 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: '0.75rem',
                    mr: 0.5,
                    userSelect: 'none',
                }}
            >
                {counterText}
            </Typography>

            {/* Prev */}
            <Tooltip title="Previous (Shift+Enter)" arrow placement="bottom">
                <span>
                    <IconButton
                        size="small"
                        disabled={matchCount === 0}
                        onClick={() => onNavigate?.('prev')}
                        aria-label="Previous match"
                        sx={{p: 0.4}}
                    >
                        <KeyboardArrowUpIcon sx={{fontSize: 18}}/>
                    </IconButton>
                </span>
            </Tooltip>

            {/* Next */}
            <Tooltip title="Next (Enter)" arrow placement="bottom">
                <span>
                    <IconButton
                        size="small"
                        disabled={matchCount === 0}
                        onClick={() => onNavigate?.('next')}
                        aria-label="Next match"
                        sx={{p: 0.4}}
                    >
                        <KeyboardArrowDownIcon sx={{fontSize: 18}}/>
                    </IconButton>
                </span>
            </Tooltip>

            {/* Close */}
            <Tooltip title="Close (Esc)" arrow placement="bottom">
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close search"
                    sx={{p: 0.4, ml: 0.25}}
                >
                    <CloseRoundedIcon sx={{fontSize: 16}}/>
                </IconButton>
            </Tooltip>
        </Paper>
    );
});

export default DocumentSearchBar;
